import React from 'react';
import { isEqual } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AlgorithmStep,
  AlgorithmControlStructure
} from '../../types/algorithm';
import {
  AddResourceAsync,
  UpdateRoutesAsync,
  UpdateGameDocState,
  GetNextResourceNameAsync,
  BuildAutoRouteTitle
} from '../../utils/game-document';
import { uuid } from '../../types/common-helper';
import Operations from '../../constants/operations';
import { AlgorithmButtons } from './algorithm-buttons';
import { IdentifierSelector } from './identifier-selector';
import { GameDocumentContext } from '../../contexts/game-document';
import { TaskRoute } from '../../types/game-document/entities/routes';
import { MultipleArgumentSelector } from './multiple-argument-selector';
import { ToastBulb } from '../map/event-configuration/event--algorithm';
import SelectionItem, { ArgumentItem } from '../../types/selection-item';
import OperationSelector, { GetArgumentList } from './operation-selector';

const compoundStatementColor = {
  and: {
    color: '#6262AB',
    border: '1px solid #6262AB',
    backgroundColor: '#6262AB5C'
  },
  then: {
    color: '#0B7C5A',
    border: '1px solid #14BF8B',
    backgroundColor: '#14BF8B80'
  },
  else: {
    color: '#5B0812',
    border: '1px solid #D22630',
    backgroundColor: '#D2263080'
  }
};

export const StepEditor: React.FC<
  AlgorithmStep & { type?: 'then' | 'else'; isAND?: boolean; idx: number }
> = (props) => {
  const { state: stateLocation, pathname } = useLocation();
  const navigate = useNavigate();

  const [state, setState] = React.useContext(GameDocumentContext);
  const [step, setStep] = React.useState<
    AlgorithmStep | AlgorithmControlStructure
  >(props);
  const [operationList, setOperationList] =
    React.useState<SelectionItem[]>(Operations);

  const cloneStep = React.useCallback(() => {
    return { ...props } as AlgorithmStep | AlgorithmControlStructure;
  }, [props]);

  const updateStep = React.useCallback(
    (modifier: (s: AlgorithmStep | AlgorithmControlStructure) => void) => {
      const newStep = cloneStep();
      modifier(newStep);
      props.onChange?.(newStep);
    },
    [cloneStep, props.onChange]
  );

  const handleIdentifierChange = React.useCallback(
    (identifier: string) =>
      updateStep((s) => {
        s.identifier = identifier;
      }),
    [updateStep]
  );

  const handleChangeOperation = React.useCallback(
    (operation: string) =>
      updateStep((s) => {
        s.operation = operation;
        delete s.argumentBoolean;

        s.argumentAssetId = '';
        s.argumentQuantity = 0;

        if (operation === 'giveScore(score)') {
          s.useFallbackValue = true;
        }

        if (operation === 'openRandomTask') {
          s.argumentBoolean = false;
        }

        if (['randomTask', 'assignTitle'].includes(operation)) {
          s.argumentQuantity = 1;
        }

        if (operation === 'resetTask') {
          s.canExecutedMultipleTimes = true;
          s.action = props.action;
        }

        if (
          ['startTimer', 'pauseTimer', 'stopTimer', 'resumeTimer'].includes(
            operation
          )
        ) {
          s.canExecutedMultipleTimes = true;
        }
      }),
    [updateStep, props.action]
  );

  const findMapAssId = (taskIds: string[]) => {
    if (!state.gameDocument) return '';

    const worldMap = state.gameDocument.rules.worldMap;
    const zones = state.gameDocument.assets.zones ?? [];

    const bothInWorldMap = taskIds.every((id) =>
      worldMap.tasks.some((t) => t.taskAssId === id)
    );
    if (bothInWorldMap) return worldMap.mapAssId;

    for (const zone of zones) {
      const bothInZone = taskIds.every((id) =>
        zone.tasks?.some((t) => t.taskAssId === id)
      );
      if (bothInZone) {
        const zoneLink = worldMap.zones.find((z) => z.zoneAssId === zone.id);
        return zoneLink?.mapAssId ?? '';
      }
    }

    return '';
  };

  const handleChangeArgument = React.useCallback(
    (argument: string | string[] | undefined) =>
      updateStep((s) => {
        s.argumentAssetId = argument;
        s.argumentQuantity = props.argumentQuantity ?? 0;

        // automatically create new annotation/route
        // indicates only for Task action (onOpen, onComplete, onClose, and onProximity)
        if (
          !s.routeId &&
          state.gameDocument &&
          s.action?.includes('Task.') &&
          ['openTask', 'showTask'].includes(s.operation ?? '') &&
          props.taskId !== argument
        ) {
          // first task id, second task id
          // task source, task destination
          const taskIds = [props.taskId ?? '', (argument as string) ?? ''];
          const mapAsId = findMapAssId(taskIds);

          if (mapAsId) {
            const routes = (state.gameDocument.assets.routes ?? []).filter(
              (r) => r.mapId === mapAsId
            );
            const findSameRoute = routes.find(
              (r) => r.fromTaskId === taskIds[0] && r.toTaskId === taskIds[1]
            );

            if (!findSameRoute) {
              const autoTitle = BuildAutoRouteTitle(
                state.gameDocument,
                props.taskId as string,
                argument as string
              );
              const titleGuid = uuid();
              AddResourceAsync(
                state.gameDocument!,
                'Annotation',
                '',
                'text',
                autoTitle,
                titleGuid
              ).then((updated) => {
                const routeId = uuid();
                const routeEntity: TaskRoute = {
                  id: routeId,
                  isVisible: true,
                  mapId: mapAsId,
                  titleResId: titleGuid,
                  fromTaskId: props.taskId as string,
                  toTaskId: argument as string,
                  name: autoTitle,
                  description: '',
                  automaticallyInfo: `Automatically created from ${s.operation === 'showTask' ? 'Show' : 'Open'} Task action`,
                  isTitleCustomized: false
                };
                s.routeId = routeId;
                updated.assets.routes = [
                  ...(updated.assets.routes ?? []),
                  routeEntity
                ];
                setState((prev) => UpdateGameDocState(prev, updated));
              });
            }
          }
        }

        // update the destination task
        if (
          s.routeId &&
          s.action?.includes('Task.') &&
          ['openTask', 'showTask'].includes(s.operation ?? '')
        ) {
          const route = (state.gameDocument?.assets.routes ?? []).find(
            (r) => r.id === step.routeId
          );

          if (route && route.fromTaskId !== argument) {
            route.toTaskId = argument as string;
            route.automaticallyInfo = `Automatically created from ${s.operation === 'showTask' ? 'Show' : 'Open'} Task action`;
            const result = UpdateRoutesAsync(state?.gameDocument!, route);
            setState((prev) => UpdateGameDocState(prev, result));
          }
        }
      }),
    [updateStep, props.argumentQuantity]
  );

  const handleChangeQuantity = React.useCallback(
    (quantity: number) =>
      updateStep((s) => {
        s.argumentQuantity = quantity;
      }),
    [updateStep]
  );

  const handleChangeCheckbox = React.useCallback(
    (isChecked: boolean) =>
      updateStep((s) => {
        if (s.operation === 'giveScore(score)') {
          s.useFallbackValue = isChecked;
        }
        s.argumentBoolean = isChecked;
      }),
    [updateStep]
  );

  const selectedOperation = React.useMemo(
    () => Operations.find((op) => op.name === props.operation),
    [props.operation]
  );

  const argumentList = React.useMemo(
    () => GetArgumentList(selectedOperation?.list, state?.gameDocument),
    [selectedOperation?.list, state.gameDocument]
  );

  const selectedArgument = React.useMemo(() => {
    if (selectedOperation?.isMultipleArgument) {
      return argumentList?.filter((i) =>
        props.argumentAssetId?.includes(i.id)
      ) as ArgumentItem[];
    } else {
      return argumentList?.find((i) =>
        props.argumentAssetId?.includes(i.id)
      ) as ArgumentItem;
    }
  }, [argumentList, props.argumentAssetId]);

  const handleDeleteStep = () => props.onDelete?.(props);
  const handleMoveUp = () => props.onMoveUp?.(props);
  const handleMoveDown = () => props.onMoveDown?.(props);
  const handleCopy = () => props.onCopy?.(props);

  const scrollToIncompleteElement = (id: string) => {
    const targetElement = document.getElementById(`step-editor-${id}`);
    if (targetElement) {
      targetElement.classList.add('blip');
      setTimeout(() => {
        targetElement.classList.remove('blip');
      }, 1000);
      targetElement.scrollIntoView({
        behavior: 'smooth'
      });
      navigate(pathname, { replace: true });
    }
  };

  const actionText = React.useMemo(() => {
    if (props.action === 'Task.onOpen')
      return 'Choose what happens every time a task is opened by a player/team.';
    if (props.action === 'Task.onComplete(score)')
      return 'Choose what happens every time a task is completed by a player/team.';
    if (props.action === 'Task.onClose')
      return 'Choose what happens every time a task is closed by a player/team.';
    if (props.action === 'Task.onProximity')
      return 'Choose what happens every time a player/team enters the range of this task.';

    if (props.action === 'Area.onEntry')
      return 'Choose what happens every time a player/team enters this area.';
    if (props.action === 'Area.onExit')
      return 'Choose what happens every time a player/team exits this area.';

    if (props.action === 'Timer.onStartTimer')
      return 'Choose what happens every time a timer starts.';
    if (props.action === 'Timer.onElapsedTimer')
      return 'Choose what happens every time a timer reaches its limit.';
    if (props.action === 'Timer.onStopTimer')
      return 'Choose what happens every time a timer stopped before it reaches its limit.';

    if (props.action === 'WorldMap.onEnter')
      return 'Choose what happens every time a player/team enters this world map.';
    if (props.action === 'WorldMap.onExit')
      return 'Choose what happens every time a player/team exits this world map.';
    if (props.action === 'WorldMap.onTaskCompleted')
      return 'Choose what happens every time a task is completed by a player/team in this world map.';

    if (props.action === 'ZoneMap.onEnter')
      return 'Choose what happens every time a player/team enters this zone map.';
    if (props.action === 'ZoneMap.onExit')
      return 'Choose what happens every time a player/team exits this zone map.';
    if (props.action === 'ZoneMap.onTaskCompleted')
      return 'Choose what happens every time a task is completed by a player/team in this zone map.';

    return '';
  }, [props.action]);

  React.useEffect(() => {
    const filtered = props.action
      ? Operations.filter(
          (item) =>
            !item.availableOnActions ||
            item.availableOnActions.includes(props.action!)
        )
      : Operations;

    setOperationList(filtered);
    setStep(props);
  }, []);

  React.useEffect(() => {
    if (!isEqual(props, step)) setStep(props);
  }, [props]);

  React.useEffect(() => {
    if (
      props.type !== undefined ||
      stateLocation?.type !== undefined ||
      stateLocation?.inCompleteStepIndex !== props.idx
    )
      return;

    scrollToIncompleteElement(props.id);
  }, [
    props.id,
    props.idx,
    props.type,
    stateLocation?.type,
    stateLocation?.inCompleteStepIndex
  ]);

  React.useEffect(() => {
    if (
      !props.type ||
      !stateLocation?.type ||
      props.type !== stateLocation.type ||
      stateLocation?.inCompleteStepIndex !== props.idx
    )
      return;

    scrollToIncompleteElement(props.id);
  }, [
    props.id,
    props.idx,
    props.type,
    stateLocation?.type,
    stateLocation?.inCompleteStepIndex
  ]);

  return (
    <>
      {props.type === undefined && actionText && (
        <div style={{ marginBottom: '16px' }}>
          <ToastBulb text={actionText} />
        </div>
      )}

      <div
        id={`step-editor-${props.id}`}
        className={`step-editor ${props.type ?? ''} d-flex flex-column`}>
        <div
          style={{
            gap: '16px',
            display: 'flex',
            overflow: 'hidden'
          }}>
          {props.type !== undefined && (
            <div
              style={{
                flexShrink: 0,
                paddingTop: '12px'
              }}>
              <span
                style={{
                  flexShrink: 0,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  fontWeight: '600',
                  borderRadius: props.isAND ? '100%' : '8px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(props.isAND
                    ? compoundStatementColor['and']
                    : compoundStatementColor[props.type])
                }}>
                {props.isAND ? 'AND' : props.type.toUpperCase()}
              </span>
            </div>
          )}

          <div
            className="d-flex flex-grow-1 flex-column gap-2"
            style={{
              overflow: 'hidden'
            }}>
            <div
              className={
                'd-flex flex-grow-1 align-items-center justify-content-between overlow-hidden'
              }
              style={{
                gap: '16px'
              }}>
              <div
                className={`d-flex flex-wrap ${props.type}`}
                style={{
                  gap: '16px'
                }}>
                <IdentifierSelector
                  identifier={props.identifier}
                  onChange={handleIdentifierChange}
                  isOnConditional={false}
                />

                {props.identifier && (
                  <OperationSelector
                    operation={props.operation}
                    operationsList={operationList}
                    argumentBoolean={props.argumentBoolean}
                    argumentAssetId={props.argumentAssetId}
                    argumentQuantity={props.argumentQuantity}
                    useFallbackValue={props.useFallbackValue}
                    onChangeOperation={handleChangeOperation}
                    onChangeArgument={handleChangeArgument}
                    onChangeQuantity={handleChangeQuantity}
                    onChangeCheckbox={handleChangeCheckbox}
                  />
                )}
              </div>

              {props.type !== undefined && (
                <AlgorithmButtons
                  handleCopy={handleCopy}
                  handleMoveUp={handleMoveUp}
                  handleMoveDown={handleMoveDown}
                  handleDeleteStep={handleDeleteStep}
                />
              )}
            </div>

            {selectedOperation !== undefined && (
              <div className={'d-flex flex-column gap-2'}>
                {selectedArgument && (
                  <div className={'d-flex align-items-center'}>
                    <span
                      className={'material-symbols-outlined'}
                      style={{
                        fontSize: '1rem'
                      }}>
                      emoji_objects
                    </span>
                    <div>
                      {selectedOperation.isMultipleArgument &&
                        `The player receives ${
                          [
                            'randomTask',
                            'openRandomTask',
                            'assignTitle'
                          ].includes(selectedOperation.name)
                            ? 'any'
                            : 'sequential'
                        } ${
                          ['openRandomTask', 'assignTitle'].includes(
                            selectedOperation.name
                          )
                            ? 1
                            : isNaN(props.argumentQuantity!) ||
                                !props.argumentQuantity
                              ? 0
                              : props.argumentQuantity
                        } ${selectedOperation.list?.includes('task') ? 'tasks' : 'titles'} from the list of specified ${selectedOperation.list?.includes('task') ? 'tasks' : 'titles'}.`}

                      {!selectedOperation.isMultipleArgument &&
                        `The '${(selectedArgument as ArgumentItem)?.title}' ${
                          selectedOperation.list
                        } ${(selectedArgument as ArgumentItem)?.description}`}
                    </div>
                  </div>
                )}

                {selectedOperation.isMultipleArgument && argumentList && (
                  <MultipleArgumentSelector
                    minimumSelected={
                      selectedOperation.name === 'assignTitle' ? 1 : 2
                    }
                    list={selectedOperation.list ?? ''}
                    argumentList={argumentList}
                    onArgumentSelected={(argument: SelectionItem[]) => {
                      const ids = (argument as SelectionItem[]).map(
                        (x) => x.id
                      );
                      handleChangeArgument(ids);
                    }}
                    selectedArgument={selectedArgument as ArgumentItem[]}
                  />
                )}

                {selectedOperation.instruction && (
                  <div className={`d-flex align-items-center`}>
                    <span
                      className={'material-symbols-outlined'}
                      style={{
                        fontSize: '1rem'
                      }}>
                      emoji_objects
                    </span>
                    <span>{selectedOperation?.instruction}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
